import type {ILine, IParseNode, IParsedFindResult, ISectionFind} from './parser-types'
import {NodeType} from './parser-types'
import {Parser, getText, getType, _ansiEscapeCodeRegex} from './parser'
import type {TemplateResult} from 'lit-html'
import {html} from 'lit-html'
import {ifDefined} from 'lit-html/directives/if-defined'

export type LogLike = string | LogWithTimestamp

interface LogWithTimestamp {
  text: string
  ts: Date
}

interface ILogLine extends IParseNode {
  text?: string
}

/** Ephemeral data object created per visible line during cluster rendering. Only lines in view are rendered; objects are GC'd after each render pass. */
interface LogLineTemplateData {
  className: string
  lineNumber: number
  lineContent: TemplateResult
  timeStamp?: string
  lineUrl: string
  isGroup: boolean
  inGroup: boolean
  isError: boolean
  isWarning: boolean
  isNotice: boolean
  groupExpanded: boolean
  // Composite action fields (populated from action markers in log stream)
  isActionStart: boolean
  inAction: boolean
  actionExpanded: boolean
  actionDisplay?: string
  actionDepth: number
  actionOutcome?: string
  actionConclusion?: string
  actionDurationMs?: number
  actionHasContent: boolean
}

const MAX_LINE_MATCHES_TO_PARSE = 100

/** Formats milliseconds into a human-readable duration string (e.g. "3s", "2m 15s", "1h 30m 5s"). */
function formatDuration(ms: number): string {
  const seconds = Math.round(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const remainderSeconds = seconds % 60
  const hours = Math.floor(minutes / 60)
  const remainderMinutes = minutes % 60
  const days = Math.floor(hours / 24)
  const remainderHours = hours % 24

  if (seconds < 0) return '0s'
  if (minutes < 1) return `${seconds}s`
  if (hours < 1) return `${minutes}m ${remainderSeconds}s`
  if (days < 1) return `${hours}h ${remainderMinutes}m ${remainderSeconds}s`
  return `${days}d ${remainderHours}h`
}

/** Returns a grayscale status icon SVG for composite action bars. Uses fgColor-danger for failure, fgColor-muted for all others. */
function actionStatusIcon(outcome?: string, conclusion?: string): TemplateResult {
  if (!conclusion) {
    // In-progress: muted spinner matching the step-level spinner pattern
    return html`<svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      fill="none"
      class="anim-rotate"
      aria-hidden="true"
      style="vertical-align: text-bottom; margin-right: 8px;"
    >
      <g stroke-width="2">
        <circle cx="8" cy="8" r="7" stroke="var(--borderColor-muted, #959da5)" />
        <path
          d="m12.9497 3.05025c1.3128 1.31276 2.0503 3.09323 2.0503 4.94975 0 1.85651-.7375 3.637-2.0503 4.9497"
          stroke="var(--fgColor-muted, var(--color-fg-muted))"
        />
      </g>
      <circle cx="8" cy="8" r="4" fill="var(--fgColor-muted, var(--color-fg-muted))" />
    </svg>`
  }
  if (outcome === 'failure' && conclusion === 'success') {
    // continue-on-error: muted check with a themed warning badge (top-left).
    // Composed from Octicons (check-circle-fill + alert-fill) with a canvas-color
    // knockout so the attention badge stays legible over the check at 16px.
    return html`<svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      style="vertical-align: text-bottom; margin-right: 8px;"
    >
      <path
        fill="var(--fgColor-muted, var(--color-fg-muted))"
        d="M8 16A8 8 0 1 1 8 0a8 8 0 0 1 0 16Zm3.78-9.72a.751.751 0 0 0-.018-1.042.751.751 0 0 0-1.042-.018L6.75 9.19 5.28 7.72a.751.751 0 0 0-1.042.018.751.751 0 0 0-.018 1.042l2 2a.75.75 0 0 0 1.06 0Z"
      />
      <circle cx="4.5" cy="4.5" r="4.75" fill="var(--bgColor-default, var(--color-canvas-default))" />
      <g transform="scale(0.5625)">
        <path
          fill="var(--fgColor-attention, var(--color-fg-attention))"
          d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0 1 14.082 15H1.918a1.75 1.75 0 0 1-1.543-2.575ZM8 5a.75.75 0 0 0-.75.75v2.5a.75.75 0 0 0 1.5 0v-2.5A.75.75 0 0 0 8 5Zm1 6a1 1 0 1 0-2 0 1 1 0 0 0 2 0Z"
        />
      </g>
    </svg>`
  }
  if (conclusion === 'success') {
    return html`<svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      style="vertical-align: text-bottom; margin-right: 8px;"
    >
      <path
        fill="var(--fgColor-muted, var(--color-fg-muted))"
        d="M8 16A8 8 0 1 1 8 0a8 8 0 0 1 0 16Zm3.78-9.72a.751.751 0 0 0-.018-1.042.751.751 0 0 0-1.042-.018L6.75 9.19 5.28 7.72a.751.751 0 0 0-1.042.018.751.751 0 0 0-.018 1.042l2 2a.75.75 0 0 0 1.06 0Z"
      />
    </svg>`
  }
  if (conclusion === 'failure') {
    return html`<svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      style="vertical-align: text-bottom; margin-right: 8px;"
    >
      <path
        fill="var(--fgColor-danger, var(--color-fg-danger))"
        fill-rule="evenodd"
        d="M2.343 13.657A8 8 0 1 1 13.658 2.343 8 8 0 0 1 2.343 13.657zM6.03 4.97a.751.751 0 0 0-1.042.018.751.751 0 0 0-.018 1.042L6.94 8 4.97 9.97a.751.751 0 0 0 .018 1.042.751.751 0 0 0 1.042.018L8 9.06l1.97 1.97a.751.751 0 0 0 1.042-.018.751.751 0 0 0 .018-1.042L9.06 8l1.97-1.97a.751.751 0 0 0-.018-1.042.751.751 0 0 0-1.042-.018L8 6.94L6.03 4.97z"
      />
    </svg>`
  }
  if (conclusion === 'cancelled') {
    return html`<svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      style="vertical-align: text-bottom; margin-right: 8px;"
    >
      <path
        fill="var(--fgColor-muted, var(--color-fg-muted))"
        fill-rule="evenodd"
        d="M4.47.22A.749.749 0 0 1 5 0h6c.199 0 .389.079.53.22l4.25 4.25c.141.14.22.331.22.53v6a.749.749 0 0 1-.22.53l-4.25 4.25A.749.749 0 0 1 11 16H5a.749.749 0 0 1-.53-.22L.22 11.53A.749.749 0 0 1 0 11V5c0-.199.079-.389.22-.53Zm.84 1.28L1.5 5.31v5.38l3.81 3.81h5.38l3.81-3.81V5.31L10.69 1.5ZM8 4a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 8 4zm0 8a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"
      />
    </svg>`
  }
  if (conclusion === 'skipped') {
    return html`<svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      style="vertical-align: text-bottom; margin-right: 8px;"
    >
      <path
        fill="var(--fgColor-muted, var(--color-fg-muted))"
        d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0ZM1.5 8a6.5 6.5 0 1 0 13 0 6.5 6.5 0 0 0-13 0Zm9.78-2.22-5.5 5.5a.749.749 0 0 1-1.275-.326.749.749 0 0 1 .215-.734l5.5-5.5a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042Z"
      />
    </svg>`
  }
  return html``
}

/** Renders line content including composite action bars (collapsible/skipped), group headers, and annotation prefixes. */
const logLineContentTemplate = (data: LogLineTemplateData) => {
  let content
  if (data.isError) {
    content = html`<span class="text-bold color-fg-danger">Error: </span>${data.lineContent}`
  } else if (data.isWarning) {
    content = html`<span class="text-bold color-fg-attention">Warning: </span>${data.lineContent}`
  } else if (data.isNotice) {
    content = html`<span class="text-bold">Notice: </span>${data.lineContent}`
  } else {
    content = data.lineContent
  }

  if (data.isActionStart) {
    const depthPx = data.actionDepth * 16
    const durationText =
      data.actionDurationMs != null && data.actionDurationMs > 0 ? formatDuration(data.actionDurationMs) : ''
    const conclusionLabel = data.actionConclusion || 'in progress'
    /* eslint eslint-comments/no-use: off */
    /* eslint-disable-next-line i18n-text/no-en -- aria label, not user-facing prose */
    const ariaLabel = `Composite action: ${data.actionDisplay || 'Action'}, ${conclusionLabel}`
    if (!data.actionHasContent) {
      // Non-clickable bar. Left padding matches chevron width (6px) + margin (6px).
      // Inter-tag whitespace becomes DOM text nodes breaking flex layout, hence:
      // prettier-ignore
      return html`<div
        style="margin-left: ${depthPx}px; background: var(--bgColor-muted, var(--color-canvas-subtle)); padding: 4px 8px 4px 12px; border-radius: 6px; display: flex; align-items: center;"
        aria-label="${ariaLabel}"
        >${actionStatusIcon(data.actionOutcome, data.actionConclusion)}<span class="text-bold color-fg-muted" style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${data.actionDisplay || 'Action'}</span></div>`
    }

    // Inter-tag whitespace becomes DOM text nodes breaking flex layout, hence:
    // prettier-ignore
    return html`<details
      class="js-checks-action-region"
      style="margin-left: ${depthPx}px; background: var(--bgColor-muted, var(--color-canvas-subtle)); padding: 4px 8px 4px 0; border-radius: 6px; cursor: pointer;"
      open="${ifDefined(data.actionExpanded ? 'open' : undefined)}"
      ><summary
        style="list-style: none; display: flex; align-items: center;"
        aria-label="${ariaLabel}"
        ><span class="action-chevron" style="margin-right: 6px;"></span>${actionStatusIcon(data.actionOutcome, data.actionConclusion)}<span class="text-bold" style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${data.actionDisplay || 'Action'}</span>${durationText
          ? html`<span class="color-fg-muted text-mono" style="margin-left: 8px; flex-shrink: 0;">${durationText}</span>`
          : html``}</summary
    ></details>`
  }

  const inActionRegion = data.inAction && !data.isActionStart
  const actionIndent = inActionRegion ? '  '.repeat(data.actionDepth + 1) : ''

  if (data.isGroup) {
    const indentCh = inActionRegion ? `${(data.actionDepth + 1) * 2}ch` : ''
    // prettier-ignore
    return html`<details class="js-checks-log-group" open="${ifDefined(data.groupExpanded ? 'open' : undefined)}"
      style="${ifDefined(indentCh ? `padding-left: ${indentCh};` : undefined)}"
      ><summary>${content}</summary></details
    >`
  }
  if (data.inGroup) {
    const groupIndent = '  '
    return html`${actionIndent}${groupIndent}${content}`
  }
  if (actionIndent) {
    return html`${actionIndent}${content}`
  }
  return content
}

const stepLogLineTemplate = (data: LogLineTemplateData) => {
  // Mark composite action-bar rows so CSS can suppress the (misaligned) timestamp
  // column and avoid clipping the taller action bar's rounded corners/hover.
  const actionRowClass = data.isActionStart ? ' CheckStep-line--action' : ''
  return html`
    <div class="js-check-step-line CheckStep-line d-flex ${data.className}${actionRowClass}">
      <a
        class="CheckStep-line-number color-fg-muted d-inline-block text-mono text-normal flex-shrink-0"
        href="${data.lineUrl}"
        >${data.lineNumber}</a
      >
      ${data.timeStamp &&
      html`<span class="CheckStep-line-timestamp color-fg-muted text-mono text-normal tmp-ml-3 flex-shrink-0"
        >${data.timeStamp}</span
      >`}
      <span class="CheckStep-line-content d-inline-block flex-auto tmp-ml-3 js-check-line-content"
        >${logLineContentTemplate(data)}</span
      >
    </div>
  `
}

const checkRunLogLineTemplate = (data: LogLineTemplateData) => html`
  <div class="CheckRun-line d-flex tmp-ml-3 ${data.className}">
    ${data.timeStamp &&
    html`<span class="CheckRun-line-timestamp text-mono text-normal tmp-ml-3 flex-shrink-0">${data.timeStamp}</span>`}
    <span class="d-inline-block flex-auto tmp-ml-3 js-check-line-content">${logLineContentTemplate(data)}</span>
  </div>
`

const pipelinesLogParser = new Parser()

export class LogParser {
  logContent: string
  rawLogData: string
  lineMetaData: ILine[]
  findResult?: IParsedFindResult
  logLines: ILogLine[]
  logLineNumbers: number[]
  timestamps: string[]
  permalinkPrefix: string
  /** Line indices of action start nodes that received conclusion data from the most recent parse. */
  completedActionLineIndices: number[]

  constructor(rawLogData: string, permalinkPrefix: string) {
    this.rawLogData = rawLogData
    this.permalinkPrefix = permalinkPrefix
    this.logContent = ''
    this.lineMetaData = []
    this.logLines = []
    this.logLineNumbers = []
    this.timestamps = []
    this.completedActionLineIndices = []
  }

  /** Strips timestamps from raw log data, parses lines into structured nodes (groups, actions, etc.), and extracts metadata. */
  updateLineMetaData() {
    const lines = this.rawLogData.split(/\r?\n/)
    // Example log line with timestamp:
    // 2019-07-11T16:56:45.9315998Z #[debug]Evaluating condition for step: 'fourth step'
    const timestampRegex = /^(.{27}Z) /

    this.timestamps = []
    this.logContent = lines
      .map(line => {
        const match = line.match(timestampRegex)
        const timestamp = match && new Date(match[1]!)

        let ts = ''
        if (match && timestamp && !isNaN(Number(timestamp))) {
          ts = timestamp.toUTCString()
          line = line.substring(match[0].length)
        }

        this.timestamps.push(ts)

        return line
      })
      .join('\n')

    const result = pipelinesLogParser.parseLines(this.logContent)
    this.lineMetaData = result.lines
    this.completedActionLineIndices = result.completedActionLineIndices
  }

  setData(data: string) {
    this.rawLogData = data
    this.updateLogLines()
  }

  appendData(data: string) {
    // TODO: we should have incremental parsing here
    this.rawLogData += data
    this.updateLogLines()
  }

  appendLines(lines: LogLike[], startLine?: number | null) {
    const offset = this.logLines.length
    const currentTime = new Date().toISOString().replace('Z', '0000Z')

    const data = lines
      .map(line => {
        const isStr = typeof line === 'string'
        const timestamp = isStr ? currentTime : line.ts.toISOString().replace('Z', '0000Z')
        const text = isStr ? line : line.text
        return `${timestamp} ${text}`
      })
      .join('\n')
    this.appendData(`${offset ? '\n' : ''}${data}`)

    if (startLine == null) return

    for (let i = 0; i < lines.length; i++) {
      this.logLineNumbers[offset + i] = startLine + i
    }
  }

  updateLogLines() {
    this.updateLineMetaData()

    this.logLines = []
    for (const line of this.lineMetaData) {
      this.logLines.push(...line.nodes)
    }
  }

  setSelectedLine(selectedLine: number) {
    if (this.findResult) this.findResult.selectedLine = selectedLine
  }

  search(text: string, selectFirstLine: boolean): number[] {
    const lines = this.findLines(text)
    const selectedLine = selectFirstLine && lines.length > 0 ? lines[0]!.line : -1
    this.findResult = {text, lines, selectedLine}
    return lines.map(line => line.line)
  }

  findLines(text: string): ISectionFind[] {
    const normalizedText = getText(text)
    const lines: ISectionFind[] = []
    for (const node of this.logLines) {
      const nodeText = this.getNodeTextForSearch(node)
      if (getText(nodeText).indexOf(normalizedText) >= 0) {
        lines.push({line: node.lineIndex})
        if (lines.length >= MAX_LINE_MATCHES_TO_PARSE) {
          return lines
        }
      }
    }
    return lines
  }

  cancelSearch() {
    this.findResult = undefined
    this.updateLogLines()
  }

  /** Called per visible line during cluster rendering. Builds an ephemeral LogLineTemplateData (GC'd after render) and renders it via the appropriate template. */
  renderTemplate(
    node: ILogLine,
    groupExpanded: boolean,
    isCheckRun: boolean,
    actionExpanded: boolean = true,
  ): TemplateResult {
    const {lineIndex} = node
    const logLineNumber = this.logLineNumbers[lineIndex]
    const lineNumber = logLineNumber != null ? logLineNumber : lineIndex + 1
    const text = this.getNodeText(node)

    // parse() does the ANSI parsing and converts to HTML
    const lineContent = pipelinesLogParser.parse(text, lineIndex, this.findResult)

    // The parser assigns a type to each line. Such as "debug" or "command".
    // These change the way the line looks. See checks.scss for the css.
    const className = `log-line-${getType(node)}`

    const data: LogLineTemplateData = {
      className,
      lineNumber,
      lineContent,
      timeStamp: this.timestamps[lineIndex],
      lineUrl: `${this.permalinkPrefix}:${lineNumber}`,
      isGroup: node.type === NodeType.Group,
      inGroup: node.group?.lineIndex != null,
      isError: node.type === NodeType.Error,
      isWarning: node.type === NodeType.Warning,
      isNotice: node.type === NodeType.Notice,
      groupExpanded,
      isActionStart: !!node.isActionStart,
      inAction: node.action != null,
      actionExpanded,
      actionDisplay: node.actionDisplay || node.action?.display,
      actionDepth: node.action?.depth ?? 0,
      actionOutcome: node.action?.outcome,
      actionConclusion: node.action?.conclusion,
      actionDurationMs: node.action?.durationMs,
      actionHasContent:
        node.actionEndLineIndex != null && node.action != null
          ? node.actionEndLineIndex - node.action.lineIndex > 1
          : false,
    }

    /**
     * Adjust displayed line numbers to account for hidden lines (##[endgroup], ##[end-action]).
     * These markers are not rendered but still count in the raw log, so without this adjustment
     * line numbers would have gaps. Keeping them aligned with raw logs matters because
     * annotation links use runner-originated line numbers.
     */
    data.lineNumber = data.lineNumber - node.hiddenLineCount

    if (isCheckRun) {
      return checkRunLogLineTemplate(data)
    } else {
      return stepLogLineTemplate(data)
    }
  }

  getNodeText(node: ILogLine) {
    if (node.text == null) {
      node.text = this.logContent.substring(node.start, node.end + 1)
    }
    return node.text
  }

  /**
   * The raw node text can contain ANSI escape codes which are not visible to users in the logs.
   * This function strips them out so that they do not cause search results to be missed
   */
  getNodeTextForSearch(node: ILogLine) {
    node.text = this.getNodeText(node)

    // Global regex pattern must be supplied if using String.replaceAll so `g` is being added to the end of the regex expression
    const regexString = new RegExp(_ansiEscapeCodeRegex, 'g')
    return node.text.replaceAll(regexString, '')
  }

  indexFromNumber(num: number) {
    const index = this.logLineNumbers.indexOf(num)
    if (index < 0) return num - 1
    return index
  }
}

export {stepLogLineTemplate}
